import {
  DerivedTableDataSourceProvider,
  type DerivedTableDataSourceState,
  type DerivedTableDataSourceValue,
} from "@/components/Debugger/DerivedTable/DerivedTableDataSourceContext";
import { useDerivedTableDebug } from "@/components/Debugger/DerivedTable/DerivedTableDebugContext";
import type React from "react";
import { useStoredDerivedTable } from "./useStoredDerivedTable";

const STORED_MESSAGES = {
  noOptionsTitleKey: "sessions.derived_table_no_data_title",
  noOptionsDescriptionKey: "sessions.derived_table_no_data_description",
  pendingTitleKey: "sessions.derived_table_loading_title",
  pendingDescriptionKey: "sessions.derived_table_loading_description",
  errorTitleKey: "sessions.derived_table_error_title",
  errorDescriptionKey: "sessions.derived_table_error_description",
};

/**
 * Drives the Session Report's derived-table view: fetches the table's already-persisted artifact
 * for this exact session version (no live solve - see useStoredDerivedTable), grouped by entity
 * instance. Unlike the live debugger, one fetch returns every instance at once, so the instance
 * picker's options come from the fetch result itself rather than a separate graph traversal.
 */
export const StoredDerivedTableDataSourceProvider = ({
  session,
  children,
}: React.PropsWithChildren<{ session: any }>) => {
  const id = session?.id ?? session?._id;
  const requestId = session?.active === false && session?.version ? `${id}-${session.version}` : id;
  const { selectedTableId, selectedInstance } = useDerivedTableDebug();

  const { data, isLoading, isError } = useStoredDerivedTable(requestId, selectedTableId);

  const instanceOptions = (data?.tables ?? []).map((group) => ({
    value: group.entityInstance ?? "global",
    label: group.entityInstance ?? "global",
  }));

  const instanceGroup = data?.tables?.find((group) => (group.entityInstance ?? "global") === selectedInstance);

  const state: DerivedTableDataSourceState = isLoading
    ? { status: "pending" }
    : isError
      ? { status: "error" }
      : instanceGroup
        ? { status: "ready", rows: instanceGroup.rows ?? [] }
        : { status: "no-options" };

  const value: DerivedTableDataSourceValue = { instanceOptions, state, messages: STORED_MESSAGES };

  return <DerivedTableDataSourceProvider value={value}>{children}</DerivedTableDataSourceProvider>;
};
